import api from "../../../utils/api";
import { unwrap, unwrapList } from "./apiEnvelope";
import { HUBSPOT_SYNC_TIMEOUT_MS } from "./hubspotService";

// ============================================================================
// Adjuntos importados de HubSpot. En HubSpot un archivo cuelga de una actividad
// (nota, correo, llamada, reunión, tarea) y la actividad es del contacto, la
// empresa y el negocio; al importarlo se copia a nuestro Storage y queda ligado
// a ese negocio y contacto. Se lee siempre con un enlace firmado que caduca.
// ============================================================================

export interface Attachment {
  id: string;
  name: string;
  contentType: string | null;
  size: number;
  /** notes | emails | calls | meetings | tasks */
  sourceType: string | null;
  /** «Nota», «Correo»… */
  sourceLabel: string | null;
  attachedAt: string | null;
  /** false cuando no se copió (pesa más de 25 MB): sólo se sabe que existe. */
  available: boolean;
  skipped: string | null;
  /** En la lista de un negocio: viene del contacto del negocio, no del negocio mismo. */
  viaContact: boolean;
}

export interface AttachmentScope {
  dealId?: string | null;
  contactId?: string | null;
  companyId?: string | null;
}

export async function fetchAttachments(scope: AttachmentScope): Promise<Attachment[]> {
  const params = Object.fromEntries(Object.entries(scope).filter(([, v]) => v));
  if (!Object.keys(params).length) return [];
  const res = await api.get("/crm/attachments", { params });
  return unwrapList<Attachment>(res);
}

/** A link to read an attachment; it stops working after a few minutes. */
export async function fetchAttachmentUrl(id: string): Promise<string> {
  const res = await api.get("/crm/attachments/file", { params: { id } });
  return (unwrap(res) as { url: string }).url;
}

export interface AttachmentsImportResult {
  found: number;
  imported: number;
  linked: number;
  already: number;
  skipped: number;
  errors: string[];
  /** Se acabó el tiempo de la petición: hay que lanzarla otra vez para continuar. */
  truncated: boolean;
  /** La clave de HubSpot no puede leer archivos. */
  missingScope: boolean;
}

/** Copy the files attached to the imported activities. Time-boxed on the server. */
export async function runAttachmentsImport(): Promise<AttachmentsImportResult> {
  const res = await api.post("/crm/hubspot/attachments/sync", {}, { timeout: HUBSPOT_SYNC_TIMEOUT_MS });
  const r = (unwrap(res) ?? {}) as Partial<AttachmentsImportResult>;
  return {
    found: r.found ?? 0,
    imported: r.imported ?? 0,
    linked: r.linked ?? 0,
    already: r.already ?? 0,
    skipped: r.skipped ?? 0,
    errors: Array.isArray(r.errors) ? r.errors : [],
    truncated: Boolean(r.truncated),
    missingScope: Boolean(r.missingScope),
  };
}

export const formatBytes = (bytes: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
